import React, { useState } from "react";
import { CheckCircle2, Wallet, Loader2, Smartphone, CreditCard, Landmark, ShieldCheck, Banknote } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { ridesApi, foodApi, inr, errorMessage } from "../../lib/api";
import { recall, remember } from "../../lib/smartDefaults";
import { SmartTag } from "./SmartDefaults";
import { paymentsApi, openCheckout, isMockOrder, LIVE_MODE } from "../../lib/razorpay";
import { useAuth } from "../../context/AuthContext";

const METHODS = [
  { id: "cash", label: "Cash", hint: "Pay the driver or delivery partner in person", icon: Banknote },
  { id: "razorpay", label: "Pay online", hint: "UPI, cards, netbanking & wallets via Razorpay", icon: Smartphone },
];

// kind: "ride" | "order"; refId: ride_id / order_id; onPaid(updatedEntity)
export function PayDialog({ open, onOpenChange, amount, kind = "ride", refId, onPaid, description }) {
  const { user } = useAuth();
  const last = recall(null, "payMethod");
  const [method, setMethod] = useState(METHODS.some((m) => m.id === last) ? last : "cash");
  const [busy, setBusy] = useState(false);
  const [mock, setMock] = useState(null); // { order } when the backend is in Razorpay test/mock mode
  const [upi, setUpi] = useState("");

  const finish = (updated) => { remember(null, "payMethod", method); onPaid(updated); toast.success(method === "cash" ? "Payment recorded" : "Payment successful"); setMock(null); onOpenChange(false); };

  const settle = async (payload) => (kind === "ride"
    ? paymentsApi.verifyRide({ ride_id: refId, ...payload })
    : foodApi.payOrder(refId, "razorpay"));

  const payOnline = async () => {
    const order = kind === "ride" ? await paymentsApi.createRideOrder(refId) : { mock: true, amount: Math.round(amount * 100), currency: "INR", ref_id: refId };
    if (isMockOrder(order)) { setMock({ order }); return; }
    const res = await openCheckout({ order, amount, user, description });
    finish(await settle({ razorpay_order_id: res.razorpay_order_id || order.order_id || `web_${refId}`, razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature || null, method: "online" }));
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (method === "cash") finish(kind === "ride" ? await ridesApi.pay(refId, "cash") : await foodApi.payOrder(refId, "cash"));
      else await payOnline();
    } catch (e) {
      if (!e?.cancelled) toast.error(e?.response ? errorMessage(e) : e?.message || "Payment failed");
    } finally { setBusy(false); }
  };

  const confirmMock = async (via) => {
    setBusy(true);
    try {
      const pid = `pay_test_${Math.random().toString(36).slice(2, 12)}`;
      finish(await settle({ razorpay_order_id: mock.order.order_id || mock.order.id || `order_test_${refId}`, razorpay_payment_id: pid, razorpay_signature: null, method: via }));
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setMock(null); onOpenChange(o); }}>
      <DialogContent className="max-w-sm" data-testid="pay-dialog">
        {!mock ? (
          <>
            <DialogHeader><DialogTitle>Pay {inr(amount)}</DialogTitle><DialogDescription>Choose how you would like to pay.</DialogDescription></DialogHeader>
            {last === method && <SmartTag testId="pay-dialog-smart" />}
            <div className="grid gap-2">
              {METHODS.map((m) => (
                <button key={m.id} type="button" onClick={() => setMethod(m.id)} className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${method === m.id ? "border-brand bg-brand-50" : "border-surface-300 hover:border-brand-200"}`} data-testid={`pay-method-${m.id}`} aria-pressed={method === m.id}>
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${method === m.id ? "bg-brand text-white" : "bg-surface-200 text-ink-soft"}`}><m.icon className="h-4 w-4" /></span>
                  <span className="min-w-0 flex-1"><span className="block text-sm font-semibold text-ink">{m.label}</span><span className="block text-xs text-ink-muted">{m.hint}</span></span>
                  {method === m.id && <CheckCircle2 className="h-4 w-4 shrink-0 text-brand" />}
                </button>
              ))}
            </div>
            {method === "razorpay" && <p className="flex items-center gap-1.5 text-[11px] text-ink-muted" data-testid="razorpay-secure-note"><ShieldCheck className="h-3.5 w-3.5 text-brand" /> Secured by Razorpay{LIVE_MODE ? "" : " (test mode)"}. Card details never touch our servers.</p>}
            <Button onClick={submit} disabled={busy} className="h-11 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="pay-confirm">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wallet className="mr-2 h-4 w-4" />}{method === "cash" ? "Confirm cash payment" : `Pay ${inr(amount)} online`}</Button>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">Razorpay <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-yellow-800" data-testid="razorpay-test-badge">Test mode</span></DialogTitle>
              <DialogDescription>Simulated checkout — no money moves. Live payments switch on when the backend gets a Razorpay key.</DialogDescription>
            </DialogHeader>
            <div className="rounded-xl bg-surface-100 p-3 text-sm"><p className="text-xs text-ink-muted">Amount</p><p className="text-xl font-bold text-ink" data-testid="razorpay-amount">{inr((mock.order.amount ?? amount * 100) / 100)}</p></div>
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">UPI ID</label>
              <Input value={upi} onChange={(e) => setUpi(e.target.value)} placeholder="name@upi" className="h-10" data-testid="razorpay-upi-input" />
              <Button onClick={() => confirmMock("upi")} disabled={busy} className="h-11 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="razorpay-pay-upi">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}<Smartphone className="mr-2 h-4 w-4" /> Pay with UPI</Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => confirmMock("card")} disabled={busy} className="rounded-full" data-testid="razorpay-pay-card"><CreditCard className="mr-2 h-4 w-4" /> Card</Button>
              <Button variant="outline" onClick={() => confirmMock("netbanking")} disabled={busy} className="rounded-full" data-testid="razorpay-pay-netbanking"><Landmark className="mr-2 h-4 w-4" /> Netbanking</Button>
            </div>
            <button type="button" onClick={() => setMock(null)} className="text-xs text-ink-muted hover:text-ink" data-testid="razorpay-back">Back to payment options</button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
